// ============================================================
// CalendarioAsistenciaDiaria — Vista mensual con leyenda.
// Reutilizable por PADRE (TabAsistenciaDiaria) y ESTUDIANTE.
// ============================================================

import React, { useMemo, useState } from 'react';
import { I } from 'components/ui';
import { useAsistenciaDiariaHistorial } from 'hooks/useAsistenciaDiaria';
import {
  AsistenciaDiariaResponse,
  EstadoAsistenciaDiaria,
  ESTADO_DIARIA_LABEL,
  ESTADO_DIARIA_BG,
  ESTADO_DIARIA_DOT,
  ESTADOS_DIARIA,
} from 'types/asistenciaDiaria.types';

const Spinner = () => (
  <svg className="animate-spin w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

// ── Helpers de fecha ──────────────────────────────────────────

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function ymd(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function ultimoDiaDelMes(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** 0 = Lunes, 6 = Domingo */
function diaSemanaLunes(year: number, month: number, day: number): number {
  const js = new Date(year, month, day).getDay(); // 0=Domingo
  return js === 0 ? 6 : js - 1;
}

// ── Componente ────────────────────────────────────────────────

interface Props {
  matriculaId: number;
}

const CalendarioAsistenciaDiaria: React.FC<Props> = ({ matriculaId }) => {
  const ahora = new Date();
  const [year, setYear]   = useState(ahora.getFullYear());
  const [month, setMonth] = useState(ahora.getMonth());

  const desde = ymd(year, month, 1);
  const hasta = ymd(year, month, ultimoDiaDelMes(year, month));

  const { registros, loading, error } = useAsistenciaDiariaHistorial(matriculaId, desde, hasta);

  // Index por fecha
  const porFecha = useMemo(() => {
    const map = new Map<string, AsistenciaDiariaResponse>();
    registros.forEach(r => map.set(r.fecha, r));
    return map;
  }, [registros]);

  // Resumen del mes
  const resumen = useMemo(() => {
    const r: Record<EstadoAsistenciaDiaria, number> = { PRESENTE: 0, TARDANZA: 0, AUSENTE: 0, JUSTIFICADO: 0 };
    registros.forEach(x => { r[x.estado]++; });
    return r;
  }, [registros]);

  // Construir matriz del mes
  const ultimoDia = ultimoDiaDelMes(year, month);
  const offset = diaSemanaLunes(year, month, 1);
  const celdas: Array<{ dia: number | null; reg: AsistenciaDiariaResponse | null }> = [];
  for (let i = 0; i < offset; i++) celdas.push({ dia: null, reg: null });
  for (let d = 1; d <= ultimoDia; d++) {
    const f = ymd(year, month, d);
    celdas.push({ dia: d, reg: porFecha.get(f) ?? null });
  }
  while (celdas.length % 7 !== 0) celdas.push({ dia: null, reg: null });

  const cambiarMes = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 0) { m = 11; y--; }
    else if (m > 11) { m = 0; y++; }
    setMonth(m);
    setYear(y);
  };

  // No avanzar a meses futuros
  const esMesFuturo = (year > ahora.getFullYear()) || (year === ahora.getFullYear() && month >= ahora.getMonth());

  return (
    <div className="space-y-5">
      {/* Header del calendario */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => cambiarMes(-1)}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Mes anterior"
          >
            <I.ChevronLeft size={18} />
          </button>
          <h3 className="text-lg font-bold text-slate-800 capitalize">
            {MESES[month]} {year}
          </h3>
          <button
            onClick={() => cambiarMes(1)}
            disabled={esMesFuturo}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Mes siguiente"
          >
            <I.ChevronRight size={18} />
          </button>
        </div>

        {error && <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : (
          <>
            {/* Encabezados de día */}
            <div className="grid grid-cols-7 gap-1 mt-4">
              {DIAS.map((d, i) => (
                <div key={i} className="text-center text-[11px] font-bold text-slate-400 uppercase py-1">{d}</div>
              ))}
            </div>
            {/* Días */}
            <div className="grid grid-cols-7 gap-1">
              {celdas.map((c, i) => {
                if (c.dia === null) return <div key={i} className="aspect-square" />;
                const reg = c.reg;
                const titulo = reg
                  ? `${ESTADO_DIARIA_LABEL[reg.estado]}${reg.horaIngreso ? ` · ${reg.horaIngreso.slice(0, 5)}` : ''}${reg.motivo ? ` · ${reg.motivo}` : ''}`
                  : 'Sin registro';
                return (
                  <div
                    key={i}
                    title={titulo}
                    className={`aspect-square rounded-xl border flex flex-col items-center justify-center text-xs font-bold transition-colors
                      ${reg
                        ? `${ESTADO_DIARIA_BG[reg.estado]} text-white border-transparent shadow-sm`
                        : 'bg-slate-50 text-slate-400 border-slate-100'
                      }`}
                  >
                    <span className="text-sm">{c.dia}</span>
                    {reg?.horaIngreso && (reg.estado === 'PRESENTE' || reg.estado === 'TARDANZA') && (
                      <span className="text-[9px] opacity-90 tabular-nums mt-0.5">
                        {reg.horaIngreso.slice(0, 5)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Leyenda + Resumen */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Leyenda</p>
          <div className="grid grid-cols-2 gap-2">
            {ESTADOS_DIARIA.map(e => (
              <div key={e} className="flex items-center gap-2 text-sm">
                <span className={`w-3 h-3 rounded-md ${ESTADO_DIARIA_DOT[e]}`} />
                <span className="text-slate-600">{ESTADO_DIARIA_LABEL[e]}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-md bg-slate-200" />
              <span className="text-slate-400">Sin registro</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Resumen del mes</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Presentes</span><span className="font-bold text-green-700">{resumen.PRESENTE}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Tardanzas</span><span className="font-bold text-amber-700">{resumen.TARDANZA}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Ausencias</span><span className="font-bold text-red-700">{resumen.AUSENTE}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Justificadas</span><span className="font-bold text-blue-700">{resumen.JUSTIFICADO}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarioAsistenciaDiaria;
